import { useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkle } from "lucide-react";
import { CoinMark } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";

const COLORS = ["#B9EBD7", "#FFD6C2", "#FFEDB8", "#CBE6F7", "#F9D3DB", "#DCD6F7", "#D8BD78"];

const CELEBRATE = {
  y: [0, -46, 0, -18, 0],
  rotateY: [0, 360, 360, 360, 360],
  scaleX: [1, 0.96, 1.12, 0.98, 1],
  scaleY: [1, 1.06, 0.88, 1.03, 1],
  transition: { duration: 1.5, ease: "easeOut", repeat: Infinity, repeatDelay: 1.2 },
};
const IDLE = { y: [0, -6, 0], rotate: [0, -4, 0], transition: { duration: 4, repeat: Infinity, ease: "easeInOut" } };

export const CoinMascot = ({ mood = "idle", size = 96 }) => (
  <div className="relative inline-flex" style={{ perspective: 600 }} data-testid={`coin-mascot-${mood}`}>
    <motion.div animate={mood === "celebrate" ? CELEBRATE : IDLE}>
      <CoinMark size={size} />
    </motion.div>
    {mood === "celebrate" && (
      <>
        <Sparkle className="sparkle-pop absolute -left-4 top-1 h-5 w-5 fill-[#D8BD78] text-[#D8BD78]" />
        <Sparkle className="sparkle-pop absolute -right-5 top-6 h-4 w-4 fill-[#87B8A5] text-[#87B8A5]" style={{ animationDelay: "0.4s" }} />
        <Sparkle className="sparkle-pop absolute -bottom-1 right-0 h-3.5 w-3.5 fill-[#F9B9C6] text-[#F9B9C6]" style={{ animationDelay: "0.8s" }} />
      </>
    )}
  </div>
);

export const Confetti = ({ count = 36 }) => {
  const pieces = useMemo(() => Array.from({ length: count }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    dx: `${(Math.random() - 0.5) * 220}px`,
    rot: `${Math.random() * 720 - 360}deg`,
    delay: `${Math.random() * 0.6}s`,
    color: COLORS[i % COLORS.length],
  })), [count]);
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((p, i) => (
        <span key={i} className="confetti-piece" style={{ left: p.left, background: p.color, animationDelay: p.delay, "--dx": p.dx, "--rot": p.rot }} />
      ))}
    </div>
  );
};

// Portalled to <body>: animated ancestors (.fade-up transform) would otherwise trap position:fixed inside the widget card
export const CelebrationOverlay = ({ open, title, text, cta = "Continuer", onClose }) => createPortal(
  <AnimatePresence>
    {open && (
      <motion.div className="fixed inset-0 z-[60] flex items-center justify-center bg-navy/40 p-6 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} data-testid="celebration-overlay">
        <Confetti />
        <motion.div className="card-surface tone-wash tone-butter relative w-full max-w-sm p-8 text-center"
          initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <CoinMascot mood="celebrate" size={104} />
          <h2 className="mt-6 text-2xl font-extrabold" data-testid="celebration-title">{title}</h2>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="celebration-text">{text}</p>
          <Button className="mt-6 w-full rounded-full" onClick={onClose} data-testid="celebration-close-button">{cta}</Button>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>,
  document.body
);
